/**
 * DualAxisChart — water elevation (left axis) + concentration bars OR
 * product thickness area (right axis). Native hand-rolled SVG (no Recharts).
 *
 * The dual-axis nature makes rectangle-zoom awkward (which y are we
 * zooming?), so this chart sticks to x-range navigation via
 * <ChartTimeRangeRow>. The existing manual YAxisRangeControl handles the
 * left axis range.
 */

import { useState, useMemo, useRef, useEffect, useCallback, useId } from 'react';
import { scaleTime, scaleLinear } from 'd3-scale';
import { line as d3Line, area as d3Area, curveMonotoneX } from 'd3-shape';
import type {
  GaugingTimeseriesResponse,
  AnalyticalTimeseriesResponse,
  ConcentrationUnit,
} from '@/api/dataVault';
import { YAxisRangeControl, parseYDomain, EMPTY_Y_RANGE, type YAxisRangeState } from './ui/YAxisRangeControl';
import { useChartView } from './interactions/useChartView';
import { useRegulatoryLines } from '../hooks/useRegulatoryLines';
import { ChartTooltip } from './ChartTooltip';
import { ChartTimeRangeRow } from './ChartTimeRangeRow';
import { defaultSelectedWellId, defaultSelectedAnalyteCode } from './chartDefaults';

interface Props {
  gaugingData: GaugingTimeseriesResponse;
  analyticalData?: AnalyticalTimeseriesResponse;
  unit: ConcentrationUnit;
}

const CONC_COLOR = '#f97316';
const ELEV_COLOR = '#60a5fa';
const PSH_COLOR = '#f59e0b';

function convertConc(val: number | null, _unit: ConcentrationUnit): number | null {
  if (val === null) return null;
  return val;
}

interface DataPoint {
  ts: number;
  date: string;
  water_elevation_ft: number | null;
  concentration: number | null;
  product_thickness_ft: number | null;
}

const MARGIN = { top: 12, right: 64, bottom: 40, left: 64 };
const CHART_HEIGHT = 420;

export function DualAxisChart({ gaugingData, analyticalData, unit }: Props) {
  const wells = useMemo(
    () => gaugingData.wells.map((w) => ({ id: w.well_id, name: w.well_name })),
    [gaugingData],
  );

  const [selectedWell, setSelectedWell] = useState<string>(() => {
    // gaugingData.wells uses { well_id, well_name }; defaultSelectedWellId takes the same shape.
    return defaultSelectedWellId(gaugingData.wells.map((w) => ({ well_id: w.well_id, well_name: w.well_name })));
  });
  const [selectedAnalyte, setSelectedAnalyte] = useState<string>(() => {
    if (!analyticalData) return '';
    return defaultSelectedAnalyteCode(analyticalData);
  });
  const [elevYRange, setElevYRange] = useState<YAxisRangeState>(EMPTY_Y_RANGE);

  const analytes = useMemo(() => {
    if (!analyticalData) return [] as { code: string; name: string }[];
    const wellName = wells.find((w) => w.id === selectedWell)?.name ?? '';
    const seen = new Map<string, string>();
    for (const w of analyticalData.wells) {
      if (w.well_id !== selectedWell && w.well_name !== wellName) continue;
      for (const [code, analyte] of Object.entries(w.analytes)) {
        if (!seen.has(code)) seen.set(code, analyte.compound || code);
      }
    }
    return Array.from(seen.entries())
      .map(([code, name]) => ({ code, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [analyticalData, selectedWell, wells]);

  const chartData: DataPoint[] = useMemo(() => {
    const wellGauging = gaugingData.wells.find((w) => w.well_id === selectedWell);
    if (!wellGauging) return [];
    const dateMap = new Map<string, DataPoint>();
    for (const m of wellGauging.measurements) {
      const ts = new Date(m.date).getTime();
      dateMap.set(m.date, {
        ts,
        date: m.date,
        water_elevation_ft: m.water_elevation_ft,
        concentration: null,
        product_thickness_ft: m.product_thickness_ft,
      });
    }
    if (analyticalData && selectedAnalyte) {
      const wellName = wells.find((w) => w.id === selectedWell)?.name ?? '';
      for (const w of analyticalData.wells) {
        if (w.well_id !== selectedWell && w.well_name !== wellName) continue;
        const analyte = w.analytes[selectedAnalyte];
        if (!analyte) continue;
        for (const m of analyte.measurements) {
          const ts = new Date(m.date).getTime();
          const existing = dateMap.get(m.date);
          if (existing) {
            existing.concentration = convertConc(m.concentration, unit);
          } else {
            dateMap.set(m.date, {
              ts,
              date: m.date,
              water_elevation_ft: null,
              concentration: convertConc(m.concentration, unit),
              product_thickness_ft: null,
            });
          }
        }
      }
    }
    return Array.from(dateMap.values()).sort((a, b) => a.ts - b.ts);
  }, [gaugingData, analyticalData, selectedWell, selectedAnalyte, unit, wells]);

  const hasPSH = useMemo(
    () => chartData.some((d) => d.product_thickness_ft != null && d.product_thickness_ft > 0),
    [chartData],
  );

  const regLineVisibleCodes = useMemo(
    () => new Set(selectedAnalyte ? [selectedAnalyte] : []),
    [selectedAnalyte],
  );
  const { lines: regulatoryLines } = useRegulatoryLines(analyticalData ?? null, regLineVisibleCodes);

  // Sizing + refs
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(800);
  const clipId = `dual-clip-${useId().replace(/:/g, '')}`;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number' && w > 0) setWidth(Math.round(w));
    });
    ro.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  const innerWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
  const innerHeight = Math.max(0, CHART_HEIGHT - MARGIN.top - MARGIN.bottom);

  const baseXDomain = useMemo<[number, number]>(() => {
    if (chartData.length === 0) return [0, 1];
    if (chartData.length === 1) return [chartData[0].ts - 86_400_000, chartData[0].ts + 86_400_000];
    return [chartData[0].ts, chartData[chartData.length - 1].ts];
  }, [chartData]);

  // X-axis viewport: click-drag rectangle zooms the time range (x-only,
  // since the dual y-axes make 2D rect zoom ambiguous), double-click resets,
  // and the preset row drives the same domain via view.setXDomain.
  const xPxToDataRef = useRef<(px: number) => number>(() => 0);
  const yPxToDataRef = useRef<(py: number) => number>(() => 0);
  const view = useChartView(
    svgRef,
    (px) => xPxToDataRef.current(px),
    (py) => yPxToDataRef.current(py),
    {
      baseXDomain,
      baseYDomain: [0, 1], // unused: drag is x-only; left/right y-scales are managed below.
      dragZoomAxes: 'x',
      chartArea: {
        top: MARGIN.top,
        bottom: MARGIN.top + innerHeight,
        left: MARGIN.left,
        right: MARGIN.left + innerWidth,
      },
    },
  );

  const xScale = useMemo(
    () => scaleTime().domain([view.xDomain[0], view.xDomain[1]]).range([MARGIN.left, MARGIN.left + innerWidth]),
    [view.xDomain, innerWidth],
  );
  xPxToDataRef.current = (px: number) => xScale.invert(px).getTime();

  // Left y-axis: water elevation.
  // parseYDomain returns ['auto','auto'] in AUTO mode (a Recharts literal this
  // hand-rolled D3 chart can't consume) — only adopt it as a fixed domain when
  // both ends are real numbers (CUSTOM mode), else auto-scale from the data.
  const customElevDomain = useMemo<[number, number] | null>(() => {
    const { domain } = parseYDomain(elevYRange);
    return typeof domain[0] === 'number' && typeof domain[1] === 'number'
      ? [domain[0], domain[1]]
      : null;
  }, [elevYRange]);
  const yLeftScale = useMemo(() => {
    let lo = Infinity, hi = -Infinity;
    for (const d of chartData) {
      if (d.water_elevation_ft != null) {
        if (d.water_elevation_ft < lo) lo = d.water_elevation_ft;
        if (d.water_elevation_ft > hi) hi = d.water_elevation_ft;
      }
    }
    if (!isFinite(lo) || !isFinite(hi)) { lo = 0; hi = 1; }
    const pad = Math.max((hi - lo) * 0.1, 0.5);
    const finalDomain = customElevDomain ?? [lo - pad, hi + pad];
    return scaleLinear().domain(finalDomain).range([MARGIN.top + innerHeight, MARGIN.top]).nice();
  }, [chartData, customElevDomain, innerHeight]);
  yPxToDataRef.current = (py: number) => yLeftScale.invert(py);

  // Right y-axis: concentration OR PSH
  const showConcAxis = !!selectedAnalyte;
  const showPSHAxis = hasPSH && !selectedAnalyte;

  const yRightScale = useMemo(() => {
    let yMax = 0;
    if (showConcAxis) {
      for (const d of chartData) if (d.concentration != null && d.concentration > yMax) yMax = d.concentration;
      for (const l of regulatoryLines) if (l.value > yMax) yMax = l.value;
    } else if (showPSHAxis) {
      for (const d of chartData) if (d.product_thickness_ft != null && d.product_thickness_ft > yMax) yMax = d.product_thickness_ft;
    }
    return scaleLinear().domain([0, yMax > 0 ? yMax * 1.1 : 1]).range([MARGIN.top + innerHeight, MARGIN.top]).nice();
  }, [chartData, regulatoryLines, showConcAxis, showPSHAxis, innerHeight]);

  // Generators
  const elevPath = useMemo(() => {
    const gen = d3Line<DataPoint>()
      .defined((d) => d.water_elevation_ft != null)
      .x((d) => xScale(d.ts))
      .y((d) => yLeftScale(d.water_elevation_ft as number))
      .curve(curveMonotoneX);
    return gen(chartData) ?? '';
  }, [chartData, xScale, yLeftScale]);

  const pshArea = useMemo(() => {
    if (!hasPSH) return '';
    const gen = d3Area<DataPoint>()
      .defined((d) => d.product_thickness_ft != null && d.product_thickness_ft > 0)
      .x((d) => xScale(d.ts))
      .y0(yRightScale(0))
      .y1((d) => yRightScale(d.product_thickness_ft as number))
      .curve(curveMonotoneX);
    return gen(chartData) ?? '';
  }, [chartData, xScale, yRightScale, hasPSH]);

  const xTicks = useMemo(() => xScale.ticks(6), [xScale]);
  const yLeftTicks = useMemo(() => yLeftScale.ticks(6), [yLeftScale]);
  const yRightTicks = useMemo(() => yRightScale.ticks(6), [yRightScale]);
  const formatX = (d: Date) => `${d.getMonth() + 1}/${d.getFullYear().toString().slice(2)}`;
  const formatLeftY = (v: number) => v.toFixed(1);
  const formatRightY = (v: number) => {
    if (v === 0) return '0';
    const abs = Math.abs(v);
    if (abs < 0.01) return v.toExponential(1);
    if (abs < 1) return v.toPrecision(2);
    return v.toFixed(1);
  };

  // Hover
  interface HoverState { point: DataPoint; clientX: number; clientY: number }
  const [hover, setHover] = useState<HoverState | null>(null);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGRectElement>) => {
      const svg = svgRef.current; if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const localX = e.clientX - rect.left;
      let best: DataPoint | null = null;
      let bestDx = Infinity;
      for (const d of chartData) {
        const px = xScale(d.ts);
        const dx = Math.abs(px - localX);
        if (dx < bestDx) {
          bestDx = dx;
          best = d;
        }
      }
      if (best && bestDx < 30) setHover({ point: best, clientX: e.clientX, clientY: e.clientY });
      else setHover(null);
    },
    [chartData, xScale],
  );
  const handleMouseLeave = useCallback(() => setHover(null), []);

  if (wells.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-mono-500 text-xs tracking-wider">NO WELL DATA AVAILABLE</div>
      </div>
    );
  }

  // Concentration bar width — function of date count
  const barWidth = Math.max(2, Math.min(14, innerWidth / Math.max(chartData.length, 1) - 2));

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <YAxisRangeControl state={elevYRange} onChange={setElevYRange} label="ELEV Y" />
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-mono-400 tracking-wider">WELL</span>
          <select
            value={selectedWell}
            onChange={(e) => setSelectedWell(e.target.value)}
            className="text-xs rounded px-2 py-1"
            style={{ background: 'var(--dv-bg-input)', borderColor: 'var(--dv-border-default)', color: 'var(--dv-text-primary)' }}
          >
            {wells.map((w) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </div>
        {analyticalData && analytes.length > 0 && (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-mono-400 tracking-wider">ANALYTE</span>
            <select
              value={selectedAnalyte}
              onChange={(e) => setSelectedAnalyte(e.target.value)}
              className="text-xs rounded px-2 py-1"
              style={{ background: 'var(--dv-bg-input)', borderColor: 'var(--dv-border-default)', color: 'var(--dv-text-primary)' }}
            >
              <option value="">None</option>
              {analytes.map((a) => (
                <option key={a.code} value={a.code}>{a.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <ChartTimeRangeRow
        dataMinMs={baseXDomain[0]}
        dataMaxMs={baseXDomain[1]}
        activeDomain={view.xDomain}
        onSelect={(d) => view.setXDomain(d)}
        isZoomed={view.isZoomed}
        onReset={view.reset}
      />

      <div ref={containerRef}>
        <svg ref={svgRef} width={width || '100%'} height={CHART_HEIGHT} style={{ display: 'block', userSelect: 'none', touchAction: 'none' }}>
          <defs>
            <clipPath id={clipId}>
              <rect x={MARGIN.left} y={MARGIN.top} width={innerWidth} height={innerHeight} />
            </clipPath>
          </defs>

          <g clipPath={`url(#${clipId})`}>
            {/* Gridlines (driven by left axis) */}
            {yLeftTicks.map((t) => {
              const y = yLeftScale(t);
              return (
                <line
                  key={`grid-${t}`}
                  x1={MARGIN.left}
                  x2={MARGIN.left + innerWidth}
                  y1={y}
                  y2={y}
                  stroke="var(--dv-chart-grid)"
                  strokeDasharray="3 3"
                  strokeWidth={1}
                />
              );
            })}

            {/* PSH area (right axis) — only when no analyte is selected.
                When an analyte IS selected, the right axis is rescaled for
                concentration (mg/L) and a PSH path drawn against that scale
                would plot feet-valued PSH against tenths-of-a-milligram,
                blowing the chart off the top of the view. */}
            {showPSHAxis && pshArea && (
              <path d={pshArea} fill={PSH_COLOR} fillOpacity={0.25} stroke={PSH_COLOR} strokeWidth={1.5} />
            )}

            {/* Concentration bars (right axis) */}
            {selectedAnalyte && chartData.map((d, i) => {
              if (d.concentration == null) return null;
              const x = xScale(d.ts);
              const y = yRightScale(d.concentration);
              const yBase = yRightScale(0);
              return (
                <rect
                  key={`bar-${i}`}
                  x={x - barWidth / 2}
                  y={y}
                  width={barWidth}
                  height={Math.max(0, yBase - y)}
                  fill={CONC_COLOR}
                  opacity={0.75}
                  rx={1}
                />
              );
            })}

            {/* Water elevation line */}
            <path d={elevPath} fill="none" stroke={ELEV_COLOR} strokeWidth={2} strokeLinejoin="round" />

            {/* Water elevation dots */}
            {chartData.map((d, i) => {
              if (d.water_elevation_ft == null) return null;
              const isHovered = hover?.point === d;
              return (
                <circle
                  key={`elev-${i}`}
                  cx={xScale(d.ts)}
                  cy={yLeftScale(d.water_elevation_ft)}
                  r={isHovered ? 4 : 2.5}
                  fill={ELEV_COLOR}
                  stroke="var(--dv-bg-card)"
                  strokeWidth={1}
                  style={isHovered ? { filter: 'url(#dv-hover-halo)' } : undefined}
                />
              );
            })}

            {/* Regulatory lines on right axis (concentration) */}
            {selectedAnalyte && regulatoryLines.map((line) => {
              const v = convertConc(line.value, unit);
              if (v === null) return null;
              const [yLo, yHi] = yRightScale.domain();
              if (v > yHi || v < yLo) return null;
              const y = yRightScale(v);
              const dashArray = line.style === 'dashed' ? '8 4' : line.style === 'dotted' ? '2 4' : undefined;
              return (
                <g key={line.key} style={{ filter: 'url(#dv-mcl-glow)' }}>
                  <line x1={MARGIN.left} x2={MARGIN.left + innerWidth} y1={y} y2={y} stroke={line.color} strokeWidth={1} strokeDasharray={dashArray} />
                  <text x={MARGIN.left + innerWidth - 4} y={y - 4} textAnchor="end" fontSize={9} fill={line.color}>
                    {`${line.label} ${line.analyteName}`}
                  </text>
                </g>
              );
            })}
          </g>

          {/* Left Y-axis (water elevation) */}
          <line x1={MARGIN.left} x2={MARGIN.left} y1={MARGIN.top} y2={MARGIN.top + innerHeight} stroke="var(--dv-chart-axis)" strokeWidth={1} />
          {yLeftTicks.map((t) => {
            const y = yLeftScale(t);
            return (
              <g key={`yl-${t}`}>
                <line x1={MARGIN.left - 4} x2={MARGIN.left} y1={y} y2={y} stroke={ELEV_COLOR} strokeWidth={1} />
                <text x={MARGIN.left - 8} y={y} textAnchor="end" dominantBaseline="middle" fontSize={11} fill={ELEV_COLOR} style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatLeftY(t)}
                </text>
              </g>
            );
          })}
          <text transform={`translate(${MARGIN.left - 48}, ${MARGIN.top + innerHeight / 2}) rotate(-90)`} textAnchor="middle" fontSize={11} fill={ELEV_COLOR}>
            Water Elev (ft)
          </text>

          {/* Right Y-axis (concentration or PSH) */}
          {(showConcAxis || showPSHAxis) && (
            <>
              <line x1={MARGIN.left + innerWidth} x2={MARGIN.left + innerWidth} y1={MARGIN.top} y2={MARGIN.top + innerHeight} stroke="var(--dv-chart-axis)" strokeWidth={1} />
              {yRightTicks.map((t) => {
                const y = yRightScale(t);
                const color = showConcAxis ? CONC_COLOR : PSH_COLOR;
                return (
                  <g key={`yr-${t}`}>
                    <line x1={MARGIN.left + innerWidth} x2={MARGIN.left + innerWidth + 4} y1={y} y2={y} stroke={color} strokeWidth={1} />
                    <text x={MARGIN.left + innerWidth + 8} y={y} textAnchor="start" dominantBaseline="middle" fontSize={11} fill={color} style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatRightY(t)}
                    </text>
                  </g>
                );
              })}
              <text transform={`translate(${MARGIN.left + innerWidth + 48}, ${MARGIN.top + innerHeight / 2}) rotate(90)`} textAnchor="middle" fontSize={11} fill={showConcAxis ? CONC_COLOR : PSH_COLOR}>
                {showConcAxis ? `Conc (${unit})` : 'PSH (ft)'}
              </text>
            </>
          )}

          {/* X-axis */}
          <line x1={MARGIN.left} x2={MARGIN.left + innerWidth} y1={MARGIN.top + innerHeight} y2={MARGIN.top + innerHeight} stroke="var(--dv-chart-axis)" strokeWidth={1} />
          {xTicks.map((t, i) => {
            const x = xScale(t);
            return (
              <g key={`xt-${i}`}>
                <line x1={x} x2={x} y1={MARGIN.top + innerHeight} y2={MARGIN.top + innerHeight + 4} stroke="var(--dv-chart-axis)" strokeWidth={1} />
                <text x={x} y={MARGIN.top + innerHeight + 16} textAnchor="middle" fontSize={11} fill="var(--dv-chart-axis)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatX(t)}
                </text>
              </g>
            );
          })}

          {/* Drag-zoom preview rectangle (x-only) */}
          {view.rectPreview && (
            <rect
              x={view.rectPreview.x0}
              y={view.rectPreview.y0}
              width={view.rectPreview.x1 - view.rectPreview.x0}
              height={view.rectPreview.y1 - view.rectPreview.y0}
              fill="rgba(96, 165, 250, 0.12)"
              stroke={ELEV_COLOR}
              strokeWidth={1}
              strokeDasharray="3 3"
              pointerEvents="none"
            />
          )}

          {/* Hit overlay */}
          <rect
            x={MARGIN.left}
            y={MARGIN.top}
            width={innerWidth}
            height={innerHeight}
            fill="transparent"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{ cursor: 'crosshair' }}
          />
        </svg>
      </div>

      {hover && (
        <ChartTooltip visible x={hover.clientX} y={hover.clientY} placement="top">
          <div style={{ minWidth: 200, fontSize: 11 }}>
            <div style={{ fontSize: 10, color: 'var(--dv-tooltip-label, rgba(255,255,255,0.7))', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
              {hover.point.date}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 12, rowGap: 2, fontFamily: 'var(--dv-font-mono, ui-monospace, monospace)' }}>
              {hover.point.water_elevation_ft !== null && (
                <>
                  <span style={{ color: ELEV_COLOR }}>Water Elev</span>
                  <span>{hover.point.water_elevation_ft.toFixed(2)} ft</span>
                </>
              )}
              {hover.point.concentration !== null && (
                <>
                  <span style={{ color: CONC_COLOR }}>Concentration</span>
                  <span>{hover.point.concentration.toFixed(3)} {unit}</span>
                </>
              )}
              {hover.point.product_thickness_ft !== null && hover.point.product_thickness_ft > 0 && (
                <>
                  <span style={{ color: PSH_COLOR }}>PSH</span>
                  <span>{hover.point.product_thickness_ft.toFixed(2)} ft</span>
                </>
              )}
            </div>
          </div>
        </ChartTooltip>
      )}
    </div>
  );
}
